import React, { useEffect, useRef } from 'react';
import { Play, Pause, Square, RotateCcw, Download } from 'lucide-react';

export function PlaybackPanel({
  isGenerating,
  isProcessingAudio,
  isPlaying,
  isPaused,
  audioUrl,
  onPlay,
  onStop,
  onDownload,
  onReset,
}) {
  const waveRef = useRef(null);
  const animFrameRef = useRef(0);

  const canPlay = !!audioUrl && !isGenerating && !isProcessingAudio;
  const isActive = isPlaying;

  useEffect(() => {
    if (!isActive) {
      if (waveRef.current) {
        const bars = waveRef.current.querySelectorAll('.wave-bar');
        bars.forEach((bar) => {
          bar.style.height = '12px';
        });
      }
      return;
    }

    const animate = () => {
      if (waveRef.current) {
        const bars = waveRef.current.querySelectorAll('.wave-bar');
        bars.forEach((bar) => {
          const h = Math.max(8, Math.random() * 64);
          bar.style.height = `${h}px`;
        });
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, [isActive]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm shrink-0">
      <div className="flex flex-col items-center gap-3">
        {/* Visualizer */}
        <div className="w-full max-w-xl">
          <div
            ref={waveRef}
            className="h-14 flex items-center justify-center gap-[3px] rounded-xl bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100 overflow-hidden"
          >
            {Array.from({ length: 40 }).map((_, i) => (
              <div
                key={i}
                className="wave-bar w-1.5 rounded-full bg-gradient-to-t from-purple-400 to-pink-400 transition-all duration-75"
                style={{ height: '12px' }}
              />
            ))}
          </div>
        </div>

        {!canPlay && !isGenerating && !isProcessingAudio && (
          <div className="text-xs text-gray-400 bg-gray-50 px-3 py-1.5 rounded-full">
            请先点击左侧「合成语音」生成音频
          </div>
        )}

        {/* Playback Controls */}
        <div className="flex items-center gap-4">
          <button
            onClick={onStop}
            disabled={!canPlay || (!isPlaying && !isPaused)}
            className="w-12 h-12 rounded-full flex items-center justify-center border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            title="停止"
          >
            <Square size={18} fill="currentColor" />
          </button>

          <button
            onClick={onPlay}
            disabled={!canPlay}
            className={`w-16 h-16 rounded-full flex items-center justify-center text-white shadow-lg transition hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 ${
              isPlaying && !isPaused
                ? 'bg-amber-500 hover:bg-amber-600'
                : 'bg-purple-600 hover:bg-purple-700'
            }`}
            title={isPlaying && !isPaused ? '暂停' : isPaused ? '继续' : '播放'}
          >
            {isPlaying && !isPaused ? (
              <Pause size={28} fill="currentColor" />
            ) : (
              <Play size={28} fill="currentColor" className="ml-1" />
            )}
          </button>

          <button
            onClick={onReset}
            className="w-12 h-12 rounded-full flex items-center justify-center border border-gray-200 text-gray-600 hover:bg-gray-50 transition"
            title="重置"
          >
            <RotateCcw size={18} />
          </button>

          <button
            onClick={onDownload}
            disabled={!canPlay}
            className="w-12 h-12 rounded-full flex items-center justify-center border border-gray-200 text-purple-600 hover:bg-purple-50 disabled:opacity-40 disabled:cursor-not-allowed transition"
            title="下载语音"
          >
            <Download size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}
